var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { getRotatedFootprint, isBoxFootprintOnPallet, palletEpsilon, } from "./pallet";
import { PalletPatternId, PatternTransform, } from "store/scene/types.current";
import { computationApiIdPoolSize } from "api/computation/constants";
/** The presets in the order they are offered in the UI. */
export var palletPatternIds = [
    PalletPatternId.SINGLE,
    PalletPatternId.BLOCK,
    PalletPatternId.ROTATED_ROWS,
    PalletPatternId.BRICK,
    PalletPatternId.PINWHEEL,
];
/*
 * Upper bound on the boxes a single pattern may produce. Boxes are not free:
 * each one takes an id from the computation API's fixed pool (see
 * `computationApiIdPoolSize`), and the rest of the pool must stay free for the
 * cup tool itself — every pump, tube, mounting and cup takes an id too. A very
 * small box on a large pallet would otherwise generate hundreds. A pattern
 * that hits the cap is truncated, which shows up as a half-filled pallet — the
 * signal that the box size is off.
 *
 * Derived, not hand-picked: boxes may claim at most a sixth of the pool
 * (255 / 6 ≈ 42, floored to a round 40), leaving over 200 ids for the tool.
 */
export var maxBoxesPerPattern = Math.min(40, Math.floor(computationApiIdPoolSize / 6));
/** How many times `size` fits into `available`, tolerating float noise. */
var fitCount = function (available, size) {
    return size <= 0 ? 0 : Math.floor((available + palletEpsilon) / size);
};
/** Centres of `count` cells of `size`, laid out centred on `centre`. */
var centredCoords = function (count, size, centre) {
    return Array.from({ length: count }, function (_, index) { return centre - (count * size) / 2 + size / 2 + index * size; });
};
/*
 * The box orientation that fits the most boxes in a plain grid. Ties go to the
 * unrotated box, so a square footprint (and the common case where the box
 * length already runs along the pallet length) stays at rotation 0.
 */
var bestGridRotation = function (boxFootprint, pallet) {
    var countFor = function (rotation) {
        var footprint = getRotatedFootprint(boxFootprint, rotation);
        return (fitCount(pallet.length, footprint.x) * fitCount(pallet.width, footprint.z));
    };
    return countFor(90) > countFor(0) ? 90 : 0;
};
/*
 * A uniform grid of identically oriented boxes, centred on the pallet. The
 * orientation is whichever fits more boxes: turning a 300×400 box sideways on a
 * 1200×800 pallet is the difference between 6 and 8 boxes.
 */
var blockPattern = function (boxFootprint, pallet, palletCentre) {
    var rotation = bestGridRotation(boxFootprint, pallet);
    var footprint = getRotatedFootprint(boxFootprint, rotation);
    var columns = fitCount(pallet.length, footprint.x);
    var rows = fitCount(pallet.width, footprint.z);
    return centredCoords(rows, footprint.z, palletCentre.z).flatMap(function (z) {
        return centredCoords(columns, footprint.x, palletCentre.x).map(function (x) { return ({
            position: { x: x, z: z },
            rotation: rotation,
        }); });
    });
};
/*
 * Bands of boxes running across the pallet, alternating orientation: the first
 * band unrotated, the next turned 90°, and so on. Bands are added while the
 * remaining pallet width can hold the next one, then the whole stack of bands
 * is centred on the pallet.
 *
 * An orientation that cannot fit (too long for the pallet, or no width left for
 * its depth) is skipped rather than ending the pattern, so a box that only fits
 * sideways still produces rows; two skips in a row means nothing more will fit.
 */
var rotatedRowsPattern = function (boxFootprint, pallet, palletCentre) {
    var bands = [];
    var usedWidth = 0;
    var index = 0;
    var consecutiveSkips = 0;
    while (consecutiveSkips < 2) {
        var rotation = index % 2 === 0 ? 0 : 90;
        var footprint = getRotatedFootprint(boxFootprint, rotation);
        var count = fitCount(pallet.length, footprint.x);
        var hasRoom = footprint.z > 0 &&
            usedWidth + footprint.z <= pallet.width + palletEpsilon;
        index++;
        if (count < 1 || !hasRoom) {
            consecutiveSkips++;
            continue;
        }
        bands.push({ rotation: rotation, footprint: footprint, count: count });
        usedWidth += footprint.z;
        consecutiveSkips = 0;
    }
    var bandStartZ = palletCentre.z - usedWidth / 2;
    return bands.flatMap(function (_a) {
        var rotation = _a.rotation, footprint = _a.footprint, count = _a.count;
        var z = bandStartZ + footprint.z / 2;
        bandStartZ += footprint.z;
        return centredCoords(count, footprint.x, palletCentre.x).map(function (x) { return ({
            position: { x: x, z: z },
            rotation: rotation,
        }); });
    });
};
/*
 * The block grid with every other row offset by half a box, so the boxes
 * interlock. The offset rows hold one box fewer and are centred, which puts
 * their boxes exactly over the seams of the rows around them — the same
 * interlock as a half-box shift, but symmetrical and always within the pallet.
 * With only one box per row there is nothing to interlock and this is a block.
 */
var brickPattern = function (boxFootprint, pallet, palletCentre) {
    var rotation = bestGridRotation(boxFootprint, pallet);
    var footprint = getRotatedFootprint(boxFootprint, rotation);
    var columns = fitCount(pallet.length, footprint.x);
    var rows = fitCount(pallet.width, footprint.z);
    return centredCoords(rows, footprint.z, palletCentre.z).flatMap(function (z, row) {
        var isOffsetRow = row % 2 === 1 && columns > 1;
        var count = isOffsetRow ? columns - 1 : columns;
        return centredCoords(count, footprint.x, palletCentre.x).map(function (x) { return ({
            position: { x: x, z: z },
            rotation: rotation,
        }); });
    });
};
/*
 * Groups of four boxes turned 90° around each other, tiled across the pallet.
 * One group occupies a square of (long side + short side), which is why the
 * pattern only really pays off when the box is about twice as long as it is
 * wide — for other proportions fewer groups fit, and the box count shown next
 * to the preset makes that visible. A square box turns this into a 2×2 block.
 */
var pinwheelPattern = function (boxFootprint, pallet, palletCentre) {
    var long = Math.max(boxFootprint.x, boxFootprint.z);
    var short = Math.min(boxFootprint.x, boxFootprint.z);
    var groupSize = long + short;
    var groupColumns = fitCount(pallet.length, groupSize);
    var groupRows = fitCount(pallet.width, groupSize);
    if (!groupColumns || !groupRows)
        return [];
    // Rotations that put the box's long side along X and along Z respectively.
    var alongX = boxFootprint.x >= boxFootprint.z ? 0 : 90;
    var alongZ = alongX === 0 ? 90 : 0;
    /* The four boxes of one group, as offsets from the group's top-left corner:
     * one along the top edge, one down the right edge, one along the bottom and
     * one up the left, each turned 90° from the last. */
    var groupOffsets = [
        { position: { x: long / 2, z: short / 2 }, rotation: alongX },
        { position: { x: long + short / 2, z: long / 2 }, rotation: alongZ },
        {
            position: { x: short + long / 2, z: long + short / 2 },
            rotation: alongX,
        },
        { position: { x: short / 2, z: short + long / 2 }, rotation: alongZ },
    ];
    return centredCoords(groupRows, groupSize, palletCentre.z).flatMap(function (groupZ) {
        return centredCoords(groupColumns, groupSize, palletCentre.x).flatMap(function (groupX) {
            var cornerX = groupX - groupSize / 2;
            var cornerZ = groupZ - groupSize / 2;
            return groupOffsets.map(function (_a) {
                var position = _a.position, rotation = _a.rotation;
                return ({
                    position: {
                        x: cornerX + position.x,
                        z: cornerZ + position.z,
                    },
                    rotation: rotation,
                });
            });
        });
    });
};
/*
 * Turn a laid-out row into the version used by the row above it. Stacks
 * interlock only when consecutive rows differ, so a row can mirror or turn the
 * pattern it is built from.
 *
 * Mirroring reflects the layout across the pallet's long axis: the bounding box
 * is unchanged, so a mirrored layout always still fits the pallet. A quarter
 * turn swaps the layout's extents, so it only fits when the pallet is square
 * enough to take it — `doesTransformFit` answers that, and boxes that would end
 * up off the pallet are dropped by the usual clip in `computePalletPattern`.
 */
export var transformPlacements = function (placements, transform, palletCentre) {
    switch (transform) {
        case PatternTransform.MIRROR:
            return placements.map(function (placement) { return ({
                position: {
                    x: placement.position.x,
                    z: 2 * palletCentre.z - placement.position.z,
                },
                // A mirrored box covers the same footprint, so its rotation is unchanged.
                rotation: placement.rotation,
            }); });
        case PatternTransform.TURN:
            return placements.map(function (placement) { return ({
                position: {
                    x: palletCentre.x + (placement.position.z - palletCentre.z),
                    z: palletCentre.z - (placement.position.x - palletCentre.x),
                },
                rotation: (placement.rotation + 90) % 180,
            }); });
        case PatternTransform.NONE:
        default:
            return placements;
    }
};
var patternGenerators = (_a = {},
    _a[PalletPatternId.SINGLE] = function (_boxFootprint, _pallet, palletCentre) { return [
        { position: { x: palletCentre.x, z: palletCentre.z }, rotation: 0 },
    ]; },
    _a[PalletPatternId.BLOCK] = blockPattern,
    _a[PalletPatternId.ROTATED_ROWS] = rotatedRowsPattern,
    _a[PalletPatternId.BRICK] = brickPattern,
    _a[PalletPatternId.PINWHEEL] = pinwheelPattern,
    _a);
/**
 * Work out where the boxes of a palletizing pattern go, for the current box
 * footprint and pallet, with the pallet centred at `palletCentre`.
 * `boxFootprint` is the box's WORLD footprint — pass `objectSize` through
 * `getBoxWorldFootprint`, whose axes are swapped relative to it.
 *
 * Boxes never hang off the pallet: any box the pattern would place partly
 * outside it is dropped, which is what leaves gaps when the box size doesn't
 * divide the pallet evenly. The result is capped at `maxBoxesPerPattern`, and
 * is never empty — a box too large for the pallet still yields the one centred
 * box, so a layer always has something in it.
 */
export var computePalletPattern = function (patternId, boxFootprint, pallet, palletCentre, transform) {
    if (transform === void 0) { transform = PatternTransform.NONE; }
    /* Built by hand rather than spread from `palletCentre`: the callers' centre
     * is usually `objectPosition`, which also carries a `y` that has no business
     * ending up in a stored box position. */
    var singleBox = [
        { position: { x: palletCentre.x, z: palletCentre.z }, rotation: 0 },
    ];
    if (boxFootprint.x <= 0 || boxFootprint.z <= 0)
        return singleBox;
    var placements = transformPlacements(patternGenerators[patternId](boxFootprint, pallet, palletCentre), transform, palletCentre).filter(function (placement) {
        return isBoxFootprintOnPallet(placement.position, getRotatedFootprint(boxFootprint, placement.rotation), pallet, palletCentre);
    });
    if (!placements.length)
        return singleBox;
    return placements.slice(0, maxBoxesPerPattern);
};
/**
 * Whether a transform keeps every box of a pattern on the pallet. A quarter
 * turn swaps the layout's extents, so on an oblong pallet it would push boxes
 * off the edge — where they are dropped, silently shrinking the row. The UI
 * uses this to refuse the transform instead.
 */
export var doesTransformFit = function (patternId, boxFootprint, pallet, palletCentre, transform) {
    return transform === PatternTransform.NONE ||
        computePalletPattern(patternId, boxFootprint, pallet, palletCentre, transform)
            .length ===
            computePalletPattern(patternId, boxFootprint, pallet, palletCentre).length;
};
/**
 * Whether two rows cover the pallet identically — the case that matters is a
 * row that repeats the row below it, because such a stack does not interlock
 * and is what makes boxes fall over.
 *
 * Compared by the space each box occupies, not by its stored rotation: a square
 * box turned a quarter turn covers exactly the same footprint, so the two rows
 * are the same stack however the rotation is recorded.
 */
export var arePlacementsEquivalent = function (a, b, boxFootprint) {
    /* Turning a layout runs the positions through arithmetic that leaves tiny
     * negatives (-5e-17) where a clean zero is meant. Formatted naively those
     * read as "-0.0000" against the other row's "0.0000", which would report two
     * identical rows as interlocking. Round first, and add zero to fold -0. */
    var coordinate = function (value) {
        return (Math.round(value * 1e4) / 1e4 + 0).toFixed(4);
    };
    var occupied = function (placements) {
        return placements
            .map(function (placement) {
            var footprint = getRotatedFootprint(boxFootprint, placement.rotation);
            return [
                coordinate(placement.position.x),
                coordinate(placement.position.z),
                coordinate(footprint.x),
                coordinate(footprint.z),
            ].join(",");
        })
            .sort()
            .join(" ");
    };
    return a.length === b.length && occupied(a) === occupied(b);
};
/** The largest pallet side the sidebar accepts, in metres. */
export var maxPalletSide = 5;
/* Interlocking variants of each pattern, offered as presets of their own. */
var variantTransforms = [PatternTransform.MIRROR, PatternTransform.TURN];
var layOutPatterns = function (boxFootprint, pallet, palletCentre) {
    return palletPatternIds.map(function (patternId) { return ({
        patternId: patternId,
        placements: computePalletPattern(patternId, boxFootprint, pallet, palletCentre),
    }); });
};
/** The earlier pattern in the list that lays out exactly like this one. */
var findLookalike = function (patternId, layouts, boxFootprint) {
    var _a;
    var index = layouts.findIndex(function (layout) { return layout.patternId === patternId; });
    return (_a = layouts
        .slice(0, index)
        .find(function (earlier) {
        return arePlacementsEquivalent(earlier.placements, layouts[index].placements, boxFootprint);
    })) === null || _a === void 0 ? void 0 : _a.patternId;
};
/*
 * Whether a pattern does something of its own: more than the one box (which
 * is what every pattern falls back to when nothing fits), laid out unlike any
 * pattern before it in the list. A brick with one box per row is a block, a
 * pinwheel without room for a whole group is the single box.
 */
var isPatternUseful = function (patternId, layouts, boxFootprint) {
    if (patternId === PalletPatternId.SINGLE)
        return true;
    var placements = layouts.find(function (layout) { return layout.patternId === patternId; }).placements;
    return (placements.length > 1 && !findLookalike(patternId, layouts, boxFootprint));
};
/*
 * The smallest pallets on which each pattern first does something of its own,
 * from the box's long and short side. Candidates only: whether one really
 * does is checked by laying the pattern out on it.
 */
var minimumPalletCandidates = function (patternId, boxFootprint) {
    var long = Math.max(boxFootprint.x, boxFootprint.z);
    var short = Math.min(boxFootprint.x, boxFootprint.z);
    var bothWays = function (length, width) { return [
        { length: length, width: width },
        { length: width, width: length },
    ]; };
    switch (patternId) {
        case PalletPatternId.BLOCK:
            // Two boxes, side by side or end to end.
            return __spreadArray(__spreadArray([], __read(bothWays(long, 2 * short)), false), __read(bothWays(2 * long, short)), false);
        case PalletPatternId.ROTATED_ROWS:
            // One band of each orientation.
            return bothWays(long, long + short);
        case PalletPatternId.BRICK:
            // Two rows of two, so the second row can be offset.
            return bothWays(2 * long, 2 * short);
        case PalletPatternId.PINWHEEL:
            // One whole group of four.
            return bothWays(long + short, long + short);
        case PalletPatternId.SINGLE:
        default:
            return [];
    }
};
/* Up to whole millimetres, so the size can be typed in as shown — floating
 * point noise (0.7000000001) must not round it up a whole millimetre. */
var ceilToMillimetre = function (metres) {
    return Math.ceil(Math.round(metres * 1e6) / 1e3) / 1e3;
};
/*
 * The smallest pallet, grown from the current one, on which a pattern does
 * something of its own — what the user would have to set for it to unlock. A
 * dimension the current pallet already has enough of is kept.
 */
var findMinimumPallet = function (patternId, boxFootprint, pallet, palletCentre) {
    return minimumPalletCandidates(patternId, boxFootprint)
        .map(function (candidate) { return ({
        length: ceilToMillimetre(Math.max(pallet.length, candidate.length)),
        width: ceilToMillimetre(Math.max(pallet.width, candidate.width)),
    }); })
        .filter(function (grown) {
        return grown.length <= maxPalletSide &&
            grown.width <= maxPalletSide &&
            isPatternUseful(patternId, layOutPatterns(boxFootprint, grown, palletCentre), boxFootprint);
    })
        .sort(function (a, b) { return a.length * a.width - b.length * b.width; })[0];
};
/**
 * The presets on offer for the current box footprint and pallet: every
 * pattern, followed by its fitting, genuinely different mirrored/turned
 * layouts.
 *
 * Every pattern stays in the list, so the user learns what exists, but one
 * that does nothing of its own here (see `isPatternUseful`) is locked, with
 * the reason — usually the pallet it would take. Hiding it would leave the
 * user no way of finding out the pallet is what holds it back; showing its
 * one-box fallback would look broken. A locked pattern offers no variants.
 */
export var getPalletPresets = function (boxFootprint, pallet, palletCentre) {
    var layouts = layOutPatterns(boxFootprint, pallet, palletCentre);
    return layouts.flatMap(function (_a) {
        var patternId = _a.patternId, placements = _a.placements;
        if (!isPatternUseful(patternId, layouts, boxFootprint)) {
            var minimumPallet = findMinimumPallet(patternId, boxFootprint, pallet, palletCentre);
            var lookalike = findLookalike(patternId, layouts, boxFootprint);
            var lock = minimumPallet
                ? { reason: "pallet_too_small", minimumPallet: minimumPallet }
                : lookalike
                    ? { reason: "same_as", patternId: lookalike }
                    : { reason: "does_not_fit" };
            return [
                { patternId: patternId, transform: PatternTransform.NONE, placements: placements, lock: lock },
            ];
        }
        var entries = [
            { patternId: patternId, transform: PatternTransform.NONE, placements: placements },
        ];
        variantTransforms.forEach(function (transform) {
            // A turned layout usually only fits a square-ish pallet; offering it
            // anyway would silently drop the boxes that fall off.
            if (!doesTransformFit(patternId, boxFootprint, pallet, palletCentre, transform)) {
                return;
            }
            // A symmetric pattern lands the same mirrored or turned (a uniform
            // block grid is the prime example) — skip lookalikes.
            var variantPlacements = computePalletPattern(patternId, boxFootprint, pallet, palletCentre, transform);
            var isDistinct = entries.every(function (entry) {
                return !arePlacementsEquivalent(entry.placements, variantPlacements, boxFootprint);
            });
            if (isDistinct) {
                entries.push({ patternId: patternId, transform: transform, placements: variantPlacements });
            }
        });
        return entries;
    });
};
/*
 * What a pattern looks like in principle, for a preset that is locked: the
 * pattern laid out with a box sized to the pallet so that every pattern shows
 * its character (long enough for a pinwheel, small enough for a few rows).
 */
export var computePatternPreview = function (patternId, pallet, palletCentre) {
    var short = Math.min(pallet.length, pallet.width);
    // Twice as long as wide, the pinwheel's own proportions; large enough that
    // the 44 px icon shows a handful of boxes rather than a texture.
    var boxFootprint = { x: short / 2, z: short / 4 };
    return {
        boxFootprint: boxFootprint,
        placements: computePalletPattern(patternId, boxFootprint, pallet, palletCentre),
    };
};
